import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { api, ApiError } from '../lib/api'
import { scan } from '../lib/liff'
import { Screen, Button, Notice } from '../components/Layout'

/**
 * QR entry — PRD v1.1 §13.
 *
 * Manual entry is a first-class path, not a hidden fallback: `scanCodeV2` is
 * unavailable in the LIFF browser on iOS and in external browsers, and a QR-first
 * product whose scanner silently fails on half its devices is broken.
 */
export default function Scan({ scannerAvailable }: { scannerAvailable: boolean }) {
  const navigate = useNavigate()
  const [manual, setManual] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(payload: string) {
    if (!payload.trim()) return
    setBusy(true)
    setError(null)
    try {
      const result = await api.parseQr(payload.trim())
      navigate('/charging/confirm', { state: result })
    } catch (e) {
      // §21 messages are already customer-legible; show them verbatim.
      setError(e instanceof ApiError ? e.message : '無法辨識，請再試一次。')
    } finally {
      setBusy(false)
    }
  }

  async function useScanner() {
    const value = await scan()
    if (value) void submit(value)
    else setError('掃描已取消，或此裝置不支援掃描，請手動輸入充電樁編號。')
  }

  return (
    <Screen title="開始充電" back="/">
      <p className="hint">請掃描充電樁上的 QR Code，或直接輸入充電樁編號。</p>

      {scannerAvailable && (
        <Button onClick={useScanner} busy={busy}>
          掃描 QR Code
        </Button>
      )}

      <label className="label" htmlFor="evse">
        充電樁編號
      </label>
      <input
        id="evse"
        className="input"
        value={manual}
        placeholder="例如 EVSE-001"
        autoCapitalize="off"
        autoCorrect="off"
        onChange={(e) => setManual(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && submit(manual)}
      />
      <Button variant="secondary" onClick={() => submit(manual)} busy={busy}>
        查詢充電樁
      </Button>

      {error && <Notice tone="error">{error}</Notice>}
    </Screen>
  )
}
